import { trackProductAction } from '@/libs/product-analytics';
import { displayRightsLaunchBlockedLines } from '@/components/display-rights/display-rights-launch-gate';
import { toaster } from '@/components/ui/toaster';
import {
  invalidateMentionBoostSetupQueries,
  updateMentionBoostSetupQueries,
} from '@/hooks/queries/use-mention-boost-setup-query';
import { invalidateCoPublicitesQueries } from '@/hooks/queries/use-co-publicites-query';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import {
  ErrorCode,
  MentionBoostDeliveryState,
  type ApiErrorResponse,
  type DisplayRightsLaunchBlockedData,
  type ApiResponse,
  type LaunchMentionBoostRequest,
  type LaunchMentionBoostResponse,
  type MentionBoostCreatorNotEligibleData,
  type MentionBoostDto,
  type MentionBoostSetupResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

// A launch whose returned delivery state is one of these is a real success: the
// boost reached Meta and is going live (or already live, or under review). A
// FAILED response is not a success, so we neither toast "launched" nor flip the
// surface to the read-only view; the form stays for retry.
const LIVE_LAUNCH_STATES = new Set<MentionBoostDeliveryState>([
  MentionBoostDeliveryState.PUBLISHING,
  MentionBoostDeliveryState.ACTIVE,
  MentionBoostDeliveryState.NEEDS_ATTENTION,
]);

interface LaunchMentionBoostVariables extends LaunchMentionBoostRequest {
  mentionId: string;
}

/**
 * The structured body of a double-validation refusal, when that is what came
 * back. Any other failure returns null and falls through to the shared
 * `errors.<CODE>` flow.
 */
function readLaunchBlocked(
  error: AxiosError<ApiErrorResponse>
): DisplayRightsLaunchBlockedData | null {
  const body = error.response?.data;
  if (body?.code !== ErrorCode.E_DISPLAY_RIGHTS_LAUNCH_BLOCKED) return null;

  const data = (body as { data?: DisplayRightsLaunchBlockedData }).data;

  return Array.isArray(data?.missing) && data.missing.length > 0 ? data : null;
}

/**
 * The creator handle a creator-eligibility refusal named, when that is what came
 * back. Meta declined on the creator's account, not the brand's, so the toast
 * says whose account it is instead of sending the marketer to inspect its own.
 *
 * Null when the server named nobody: the shared `errors.<CODE>` flow then writes
 * the sentence without a handle, which is better than one with a hole in it.
 */
function readCreatorNotEligible(error: AxiosError<ApiErrorResponse>): string | null {
  const body = error.response?.data;
  if (body?.code !== ErrorCode.E_MENTION_BOOST_LAUNCH_CREATOR_NOT_ELIGIBLE) return null;

  const username = (body as { data?: MentionBoostCreatorNotEligibleData }).data?.creatorUsername;

  return username ? `@${username.replace(/^@/, '')}` : null;
}

const launchMentionBoost = async ({
  mentionId,
  ...payload
}: LaunchMentionBoostVariables): Promise<ApiResponse<LaunchMentionBoostResponse>> => {
  const response = await api.post<ApiResponse<LaunchMentionBoostResponse>>(
    `/dashboard/mentions/${mentionId}/boost-launch`,
    payload
  );

  return response.data;
};

/**
 * Launches a prepared mention boost. On success it primes the boost-setup
 * cache with the launched boost so `BoostDeliveryStatus` renders immediately,
 * then invalidates to reconcile with the server. Launch failures are toasted
 * through the shared `errors.<CODE>` translation flow.
 */
export const useLaunchMentionBoost = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<LaunchMentionBoostResponse>,
    AxiosError<ApiErrorResponse>,
    LaunchMentionBoostVariables
  >({
    mutationFn: (input) => trackProductAction('co_ads', 'publish', () => launchMentionBoost(input)),
    onSuccess: (response, variables) => {
      const launchedBoost: MentionBoostDto | null = response.data?.boost ?? null;
      const isLive = !!launchedBoost && LIVE_LAUNCH_STATES.has(launchedBoost.deliveryState);

      // A FAILED launch is not a success: keep the marketer on the form to retry
      // instead of toasting "launched" and flipping to the read-only view.
      if (isLive) {
        toaster.success({
          title: t('mentions.boost.launch.success'),
        });

        // Prime the boost-setup cache so the launched view renders immediately,
        // even when nothing was cached yet (cold cache). Constructing the full
        // shape keeps the launched boost from being dropped on an undefined
        // previous, which left both the save and launch CTAs visible mid-flight.
        updateMentionBoostSetupQueries(
          queryClient,
          variables.mentionId,
          (previous) =>
            ({
              setup: previous?.setup ?? null,
              eligibility: previous?.eligibility,
              readiness: previous?.readiness ?? null,
              launchedBoost,
            }) as MentionBoostSetupResponse
        );
      }

      void invalidateMentionBoostSetupQueries(queryClient, variables.mentionId);
      void invalidateCoPublicitesQueries(queryClient);
    },
    onError: (error, variables) => {
      const blocked = readLaunchBlocked(error);

      if (blocked) {
        // Say which side the SERVER refused on, not which side the panel thought
        // was missing. The two diverge exactly when something moved between the
        // render and the click, and that is when a generic toast helps least.
        toaster.error({
          title: t('mentions.boost.launch.gate.blocked.title'),
          description: displayRightsLaunchBlockedLines(blocked)
            .map((line) =>
              t(line.key, {
                ...line.params,
                state: line.params?.state
                  ? t(`displayRights.status.${line.params.state}`)
                  : undefined,
              })
            )
            .join(' '),
        });

        // The panel is now behind: re-read so the checklist shows what the
        // server just said instead of the state that got refused.
        void invalidateMentionBoostSetupQueries(queryClient, variables.mentionId);

        return;
      }

      const ineligibleCreator = readCreatorNotEligible(error);

      if (ineligibleCreator) {
        toaster.error({
          title: t('common.error'),
          description: t(`errors.${ErrorCode.E_MENTION_BOOST_LAUNCH_CREATOR_NOT_ELIGIBLE}`, {
            creator: ineligibleCreator,
            defaultValue: error.response?.data?.message,
          }),
        });

        return;
      }

      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'common.error'),
      });
    },
  });
};
